import { SystemHealth, DashboardLogEntry, NotificationSeverity } from '@/types';
import { apiClient } from './client';
import { useNotificationStore } from '@/store/useNotificationStore';
import { mapNotificationSeverityToLevel } from '@/utils/severity';

/**
 * System Health API service
 */
export const healthApi = {
  /**
   * Get overall system health
   */
  async getSystemHealth(): Promise<SystemHealth> {
    const { data } = await apiClient.get<SystemHealth>('/health');
    return data;
  },




  /**
   * Get service status from SSE notification store
   * @deprecated Use notification store directly
   */
  async getServiceStatus(): Promise<{
    services: Array<{
      name: string;
      status: 'healthy' | 'degraded' | 'unhealthy';
      lastChecked: string;
      details?: string;
    }>;
    _meta?: {
      isStale: boolean;
      lastUpdate: string | null;
      ageMs: number;
    };
  }> {
    const store = useNotificationStore.getState();
    const freshness = store.getDataFreshness();

    return {
      services: Array.from(store.serviceHealth.values()),
      _meta: {
        isStale: freshness.isStale,
        lastUpdate: freshness.lastUpdate,
        ageMs: freshness.ageMs,
      },
    };
  },

  /**
   * Get metrics from SSE notification store
   * @deprecated Use notification store directly
   */
  async getMetrics(): Promise<{
    cpu: number | null;
    memory: number | null;
    storage: {
      used: number | null;
      total: number | null;
    };
    workers?: {
      total: number;
      byModule: Record<string, number>;
    };
    cosmosDb?: {
      provisionedRUs: number;
      consumedRUs: number;
      utilizationPercent: number;
      isThrottling: boolean;
    };
    systemCapacity?: {
      status: string;
      bottleneck?: string;
    };
    _meta?: {
      isStale: boolean;
      lastUpdate: string | null;
      ageMs: number;
    };
  }> {
    const store = useNotificationStore.getState();
    const freshness = store.getDataFreshness();
    const workerCount = store.workerHealth.size;

    // Aggregate workers by module
    const byModule: Record<string, number> = {};
    for (const worker of store.workerHealth.values()) {
      const moduleId = worker.moduleId || 'unknown';
      byModule[moduleId] = (byModule[moduleId] || 0) + 1;
    }

    return {
      cpu: null,
      memory: null,
      storage: { used: null, total: null },
      workers: {
        total: workerCount,
        byModule,
      },
      cosmosDb: store.systemMetrics.cosmosDb,
      systemCapacity: undefined,
      _meta: {
        isStale: freshness.isStale,
        lastUpdate: freshness.lastUpdate,
        ageMs: freshness.ageMs,
      },
    };
  },

  /**
   * Get system events from SSE notification store
   * @deprecated Use notification store directly
   */
  async getSystemEvents(limit = 50): Promise<{
    events: DashboardLogEntry[];
    _meta?: {
      isStale: boolean;
      lastUpdate: string | null;
      ageMs: number;
    };
  }> {
    const store = useNotificationStore.getState();
    const freshness = store.getDataFreshness();

    const events: DashboardLogEntry[] = store.getRecentErrors(limit).map(n => ({
      timestamp: n.timestamp || new Date().toISOString(),
      level: mapNotificationSeverityToLevel(n.severity as NotificationSeverity),
      service: n.source || 'unknown',
      message: n.message || 'No message provided',
      details: n.metrics,
    }));

    return {
      events,
      _meta: {
        isStale: freshness.isStale,
        lastUpdate: freshness.lastUpdate,
        ageMs: freshness.ageMs,
      },
    };
  },

  /**
   * Trigger health check for specific service
   */
  async checkService(serviceName: string): Promise<{
    service: string;
    status: 'healthy' | 'degraded' | 'unhealthy';
    responseTime: number;
    details?: string;
  }> {
    const { data } = await apiClient.post(`/health/check/${serviceName}`);
    return data;
  },

  /**
   * Get queue statistics
   */
  async getQueueStatistics(): Promise<{
    queues: Array<{
      name: string;
      active_message_count: number;
      dead_letter_message_count: number;
      total_message_count: number;
      size_in_bytes: number;
      accessed_at: string | null;
      updated_at: string | null;
      error?: string | null;
    }>;
    total_active_messages: number;
    total_dead_letter_messages: number;
    total_messages: number;
  }> {
    const { data } = await apiClient.get('/health/queues');
    return data;
  },

  /**
   * Clear all messages from all queues (including dead-letter queues)
   * Requires initial admin permissions
   */
  async clearAllQueues(): Promise<{
    status: string;
    message: string;
    total_messages_cleared: number;
    queues: Record<string, number | string>;
  }> {
    const { data } = await apiClient.delete('/cleanup/queues');
    return data;
  },
};
